import { useEffect, useRef, useState } from "react";

// 서버 발송 제한(30분)이 어떤 이유로 동작하지 않을 때를 대비한
// 클라이언트 측 재전송 쿨다운 시간(초).
// localStorage 에 저장되므로 창을 닫았다 다시 열어도 유지됩니다.
export const MAIL_SEND_COOLDOWN_SECONDS = 60;

const STORAGE_PREFIX = "mailSendCooldownUntil:";

// 주어진 키(이메일)에 대해 남은 쿨다운 시간(초)을 반환합니다. (없으면 0)
function readRemaining(key: string): number {
  if (!key) return 0;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key.toLowerCase());
    if (!raw) return 0;
    const until = Number(raw);
    if (!until || Number.isNaN(until)) return 0;
    const remaining = Math.ceil((until - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  } catch {
    return 0;
  }
}

// 이메일 인증 메일 재전송 쿨다운을 관리하는 훅.
// - remaining: 남은 쿨다운(초). 0 이면 전송 가능.
// - check(email): 해당 이메일의 저장된 쿨다운을 확인해 remaining 을 갱신하고 남은 시간을 반환.
// - start(email): 전송 성공 직후 호출하여 쿨다운을 시작(저장)합니다.
export function useSendCooldown() {
  const [remaining, setRemaining] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  function stop() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  function runInterval() {
    stop();
    intervalRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          stop();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  // 언마운트 시 타이머 정리
  useEffect(() => {
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function check(key: string): number {
    const rem = readRemaining(key);
    setRemaining(rem);
    if (rem > 0) runInterval();
    else stop();
    return rem;
  }

  function start(key: string, seconds: number = MAIL_SEND_COOLDOWN_SECONDS) {
    try {
      localStorage.setItem(STORAGE_PREFIX + key.toLowerCase(), String(Date.now() + seconds * 1000));
    } catch {
      // localStorage 사용 불가 시 무시 (메모리 상 카운트다운만 동작)
    }
    setRemaining(seconds);
    runInterval();
  }

  return { remaining, start, check };
}
